/**
 * Système Parent-Enfant — un mineur n'a jamais de compte propre : le parent (patient majeur déjà
 * authentifié) crée et gère un profil enfant rattaché au sien.
 */

import { apiFetch } from "./client";

export type TypeLienParentEnfant = "parent" | "tuteur_legal" | "autre";
export type StatutLiaisonParentEnfant = "active" | "revoquee";
export type StatutVerificationFiliation = "non_soumis" | "en_attente" | "validee" | "rejetee";

export type LiaisonParentEnfant = {
  id: string;
  parentUserId: string;
  parentNip: string;
  enfantNip: string;
  typeLien: TypeLienParentEnfant;
  statut: StatutLiaisonParentEnfant;
  justificatifFiliationUrl: string | null;
  statutVerificationFiliation: StatutVerificationFiliation;
  dateSoumissionFiliation: string | null;
  dateTraitementFiliation: string | null;
  verificateurId: string | null;
  motifRejetFiliation: string | null;
  dateCreation: string;
  dateRevocation: string | null;
  revoquePar: string | null;
  motifRevocation: string | null;
};

export type EnfantSummary = {
  nip: string;
  nom: string;
  prenom: string;
  dateNaissance: string;
};

export type LiaisonParentEnfantListItem = {
  liaison: LiaisonParentEnfant;
  enfant: EnfantSummary;
};

type ApiResult<T> = { ok: true; data: T } | { ok: false; message: string; errorCode?: string };

function unwrap<T>(payload: unknown): T {
  const p = payload as { data?: unknown } & Record<string, unknown>;
  return (p?.data ?? p) as T;
}

export async function createEnfant(body: {
  nom: string;
  prenom: string;
  dateNaissance: string;
  nip: string;
  justificatifFiliationUrl: string;
  typeLien?: TypeLienParentEnfant;
}): Promise<ApiResult<LiaisonParentEnfantListItem>> {
  const out = await apiFetch<unknown>("/patient/patients/enfants", {
    method: "POST",
    body: JSON.stringify(body),
  });
  if (!out.ok) return { ok: false, message: out.message, errorCode: out.errorCode };
  return { ok: true, data: unwrap<LiaisonParentEnfantListItem>(out.data) };
}

export async function listMesEnfants(): Promise<ApiResult<LiaisonParentEnfantListItem[]>> {
  const out = await apiFetch<unknown>("/patient/patients/mes-enfants");
  if (!out.ok) return { ok: false, message: out.message, errorCode: out.errorCode };
  const data = unwrap<LiaisonParentEnfantListItem[]>(out.data);
  return { ok: true, data: Array.isArray(data) ? data : [] };
}

export async function soumettreFiliation(
  liaisonId: string,
  justificatifFiliationUrl: string
): Promise<ApiResult<LiaisonParentEnfant>> {
  const out = await apiFetch<unknown>(`/patient/liaisons-parent-enfant/${encodeURIComponent(liaisonId)}/filiation`, {
    method: "POST",
    body: JSON.stringify({ justificatifFiliationUrl }),
  });
  if (!out.ok) return { ok: false, message: out.message, errorCode: out.errorCode };
  return { ok: true, data: unwrap<LiaisonParentEnfant>(out.data) };
}

/** Bypass d'urgence — appelé côté professionnel/établissement, pas côté patient. */
export async function declencherAccesUrgence(
  enfantNip: string,
  motif: string
): Promise<ApiResult<{ granted: boolean }>> {
  const out = await apiFetch<unknown>(`/patient/patients/${encodeURIComponent(enfantNip)}/access/urgence`, {
    method: "POST",
    body: JSON.stringify({ motif }),
  });
  if (!out.ok) return { ok: false, message: out.message, errorCode: out.errorCode };
  return { ok: true, data: unwrap<{ granted: boolean }>(out.data) };
}
